import {
	Button,
	Elem,
	ElemBefore,
	IconButton,
	TextInput,
} from "@alm/flow-ds/components";
import BusinessLogicContext, {
	BusinessLogicContextProps,
} from "contexts/BusinessLogicContext";
import { capitalize, startCase } from "lodash";
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { logicType } from "schemas/elements";
import { TextValidation } from "validation/validation";

export type fields = {
	fieldOne: string | undefined;
	fieldTwo: string | undefined;
};

interface Props {
	nextProp: "dataMap" | "logic";
	values: logicType["params"] | string[][];
	reformatValues: (vals: fields[]) => logicType[] | string[][];
	logicIndex?: number;
	hint: string;
	paramLabels?: string[];
}

export default ({
	nextProp,
	values,
	reformatValues,
	logicIndex = 0,
	hint,
	paramLabels,
}: Props) => {
	const { t } = useTranslation();

	const newItem = useMemo(() => {
		return { fieldOne: undefined, fieldTwo: undefined };
	}, []);

	const [init, setInit] = useState(false);

	const { action, handleUpdateStore, elementIndex, validation, value } =
		useContext(BusinessLogicContext) as BusinessLogicContextProps;

	const handleFormatValues = useCallback(() => {
		const v =
			nextProp === "logic"
				? Object.entries(values as logicType["params"])
				: (values as string[][]);

		return v.map(val => {
			return { fieldOne: val[0], fieldTwo: val[1] };
		});
	}, [nextProp, values]);

	const [fmtdValues, setFmtdValues] = useState<fields[]>(
		handleFormatValues() || []
	);

	const handleChange = useCallback(
		(e, index, field: keyof fields) => {
			const currentValues = [...fmtdValues];
			currentValues[index][field] = e.currentTarget?.value;
			setFmtdValues(currentValues);
		},
		[fmtdValues]
	);

	const handleBlur = useCallback(() => {
		const handledValues = reformatValues(fmtdValues);
		handleUpdateStore({ ...value, [nextProp]: [...handledValues] });
	}, [reformatValues, fmtdValues, handleUpdateStore, value, nextProp]);

	const handleAdd = useCallback(() => {
		setFmtdValues([...fmtdValues, newItem]);
	}, [fmtdValues, newItem]);

	const handleRemove = useCallback(
		(index: number) => {
			const currentValues = [...fmtdValues];
			currentValues.splice(index, 1);
			setFmtdValues(currentValues);
		},
		[fmtdValues]
	);

	useEffect(() => {
		if (init) {
			const handledValues = reformatValues(fmtdValues);
			const hv =
				nextProp === "logic"
					? (handledValues as logicType[])
					: (handledValues as string[][]);

			handleUpdateStore({
				...value,
				[nextProp]: hv,
				...(nextProp === "dataMap"
					? { allData: !!handledValues.length }
					: {}),
			});
		} else setInit(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fmtdValues.length]);

	useEffect(() => {
		setFmtdValues(handleFormatValues());
	}, [handleFormatValues, nextProp, values]);

	return (
		<Elem className="elem-twin-input">
			<ElemBefore
				label={
					nextProp === "logic"
						? t("formDesigner.labels.parameters")
						: t("formDesigner.labels.dataMap")
				}
			/>
			<div className="hint-text">{hint}</div>
			<div
				className={`elem-twin-input-properties${
					nextProp === "logic" && fmtdValues?.length < 1
						? " disabled"
						: ""
				}`}>
				{nextProp === "logic" &&
					fmtdValues?.length < 1 &&
					t("formDesigner.placeholders.logicRelation")}
				{fmtdValues?.map((item: fields, index: number) => {
					return (
						<div className="elem-twin-input-item" key={index}>
							{nextProp !== "logic" && (
								<TextInput
									id={`${action}-${elementIndex}-${nextProp}-${index}-${logicIndex}-fieldOne`}
									label={t(
										"formDesigner.placeholders.filter"
									)}
									onChange={e =>
										handleChange(e, index, "fieldOne")
									}
									value={item.fieldOne}
									labelHidden={true}
									placeholder={t(
										"formDesigner.placeholders.filter"
									)}
									onBlur={handleBlur}
									status={
										validation &&
										!TextValidation(item.fieldOne)
											? "error"
											: undefined
									}
									required={true}
								/>
							)}
							<TextInput
								id={`${action}-${elementIndex}-${nextProp}-${index}-${logicIndex}-fieldTwo`}
								onChange={e =>
									handleChange(e, index, "fieldTwo")
								}
								required={
									nextProp === "logic" &&
									paramLabels !== undefined &&
									paramLabels[index] !== undefined &&
									paramLabels[index] !== null
										? !paramLabels[index].includes(
												" - optional"
										  )
										: false
								}
								key={index}
								label={
									nextProp === "logic"
										? `${startCase(item.fieldOne)}${
												paramLabels !== undefined &&
												paramLabels[index] !== undefined
													? `: ${capitalize(
															paramLabels[
																index
															].replace(
																" - optional",
																""
															)
													  )}`
													: ""
										  }`
										: t(
												"formDesigner.placeholders.mapOptional"
										  )
								}
								labelHidden={
									nextProp === "logic" ? false : true
								}
								value={item.fieldTwo}
								placeholder={
									nextProp === "logic"
										? t("formDesigner.placeholders.value")
										: t(
												"formDesigner.placeholders.mapOptional"
										  )
								}
								onBlur={handleBlur}
								status={
									nextProp === "logic" &&
									validation &&
									!TextValidation(item.fieldTwo)
										? "error"
										: undefined
								}
							/>
							{(nextProp !== "logic" ||
								(nextProp === "logic" &&
									paramLabels !== undefined &&
									paramLabels[index] !== undefined &&
									paramLabels[index] !== null &&
									paramLabels[index].includes(
										" - optional"
									))) && (
								<IconButton
									label={t("formDesigner.labels.remove")}
									icon="times"
									onClick={() => handleRemove(index)}
									className={`btn-outline-danger${
										nextProp === "logic" &&
										paramLabels !== undefined &&
										paramLabels[index] !== undefined &&
										paramLabels[index] !== null &&
										paramLabels[index].includes(
											" - optional"
										)
											? " logic-optional"
											: ""
									}`}
								/>
							)}
						</div>
					);
				})}
				{nextProp !== "logic" && (
					<Button
						label={t("formDesigner.labels.add")}
						onClick={handleAdd}
						className="btn-default"
					/>
				)}
			</div>
		</Elem>
	);
};
